import React from "react"
import { graphql } from "gatsby"
import { kebabCase } from "lodash"

import ImageTextSection from "../sections/ImageText/ImageTextSection"
import ImageSection from "../sections/Image"
import Cta from "../sections/Cta"
import CtaInfoCards from "../sections/CtaInfoCards"
import InfoCards from "../sections/InfoCards"
import Twitter from "../sections/Twitter"
import ButtonGrid from "../sections/ButtonGrid"
import BlogGrid from "../sections/BlogGrid"
import NewsletterSignup from "../sections/NewsletterSignup"
import TeamGrid from "../sections/TeamGrid"
import TeamMember from "../sections/TeamMember"
import LogoGrid from "../sections/LogoGrid"
import ImageCarousel from "../sections/ImageCarousel"
import Stats from "../sections/Stats"
import Contact from "../sections/Contact"
import Careers from "../sections/Careers"
import CareersAts from "../sections/CareersAts"
import Pricing from "../sections/Pricing"
import PricingGrid from "../sections/PricingGrid"
import ContactForm from "../sections/ContactForm"
import DynamicTitle from "../sections/DynamicTitle"
import LogoCarousel from "../sections/LogoCarousel"
import CustomHtml from "../sections/CustomHtml"
import CustomSection from "../sections/CustomSection"
import PortfolioGrid from "../sections/PortfolioGrid"
import Article from "../sections/Article"
import QuoteCarousel from "../sections/QuoteCarousel"
import ImageTextCarousel from "../sections/ImageTextCarousel/ImageTextCarousel"
import Faqs from "../sections/Faqs"
import ImageGrid from "../sections/ImageGrid"
import ImageCards from "../sections/ImageCards"
import BigImageCards from "../sections/BigImageCards"
import InteractiveInfoCards from "../sections/InteractiveInfoCards"
import Text from "../sections/Text"
import ThinCta from "../sections/ThinCta"
import FlexColumns from "../sections/FlexColumns"
import ToggleList from "../sections/ToggleList"

/* webpackPrefetch will ask the browser to start fetching it asap */
// Render all page sections in Gatsby's initial HTML so non-JS crawlers can read them.
const sectionMap = {
  "sections.image-text": ImageTextSection,
  "sections.image": ImageSection,
  "sections.cta": Cta,
  "sections.cta-info-cards": CtaInfoCards,
  "sections.info-cards": InfoCards,
  "sections.twitter": Twitter,
  "sections.button-grid": ButtonGrid,
  "sections.blog-grid": BlogGrid,
  "sections.newsletter-signup": NewsletterSignup,
  "sections.team-grid": TeamGrid,
  "sections.team-member": TeamMember,
  "sections.logo-grid": LogoGrid,
  "sections.image-carousel": ImageCarousel,
  "sections.stats": Stats,
  "sections.contact": Contact,
  "sections.careers": Careers,
  "sections.careers-ats": CareersAts,
  "sections.pricing": Pricing,
  "sections.pricing-grid": PricingGrid,
  "sections.contact-form": ContactForm,
  "sections.dynamic-title": DynamicTitle,
  "sections.logo-carousel": LogoCarousel,
  "sections.custom-html": CustomHtml,
  "sections.custom-section": CustomSection,
  "sections.portfolio-grid": PortfolioGrid,
  "sections.article": Article,
  "sections.quote-carousel": QuoteCarousel,
  "sections.image-text-carousel": ImageTextCarousel,
  "sections.faqs": Faqs,
  "sections.image-grid": ImageGrid,
  "sections.image-cards": ImageCards,
  "sections.big-image-cards": BigImageCards,
  "sections.interactive-info-cards": InteractiveInfoCards,
  "sections.text": Text,
  "sections.thin-cta": ThinCta,
  "sections.flex-columns": FlexColumns,
  "sections.toggle-list": ToggleList,
}

// helper to turn Strapi typename into the key we use in sectionMap
const mapTypeToKey = section => {
  if (section.__component) {
    return section.__component.startsWith("sections.")
      ? section.__component // Next.js shape
      : `sections.${section.__component}` // Gatsby shape
  }
  if (section.__typename)
    return `sections.${kebabCase(section.__typename.slice(27))}`
  return null
}

export const getSectionId = section => {
  if (section.__component && section.strapi_id)
    return `sections.${section.__component}.${section.strapi_id}`
  if (section.__component && typeof section.id === "number")
    return `${section.__component}.${section.id}`
  if (section.__component && typeof section.id === "string") return section.id // when section has placeholder id, the __component value isn't included in id value in redux state
  if (section.__typename)
    return `sections.${kebabCase(section.__typename.slice(27))}.${
      section.id || section.strapi_id
    }`
  // slice(27) removes "STRAPI__COMPONENT_SECTIONS_"
  else return ""
}

const Sections = ({ section, index = 0, ssrCount = 0 }) => {
  // skip section if set to be hidden
  if (section.section?.hidden) return null

  const key = mapTypeToKey(section)
  const Component = key ? sectionMap[key] : null
  if (!Component) return null // return early if section is not found

  const id = getSectionId(section)
  const sectionProps = section.section
    ? {
        ...section,
        section: {
          ...section.section,
          sectionId: id,
        },
      }
    : section

  const eager = index < ssrCount

  return (
    <section id={id} style={{ margin: 0, padding: 0 }} suppressHydrationWarning>
      <Component {...sectionProps} eagerLoad={eager && index === 0} />
    </section>
  )
}

export default Sections

export const query = graphql`
  fragment Sections on PageSectionsDynamicZone {
    __typename
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE_TEXT {
      ...ImageTextSection
    }
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE {
      ...ImageSection
    }
    ... on STRAPI__COMPONENT_SECTIONS_CTA {
      ...Cta
    }
    ... on STRAPI__COMPONENT_SECTIONS_TWITTER {
      ...Twitter
    }
    ... on STRAPI__COMPONENT_SECTIONS_INFO_CARDS {
      ...InfoCards
    }
    ... on STRAPI__COMPONENT_SECTIONS_BUTTON_GRID {
      ...ButtonGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_BLOG_GRID {
      ...BlogGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_NEWSLETTER_SIGNUP {
      ...NewsletterSignup
    }
    ... on STRAPI__COMPONENT_SECTIONS_TEAM_GRID {
      ...TeamGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_TEAM_MEMBER {
      ...TeamMember
    }
    ... on STRAPI__COMPONENT_SECTIONS_LOGO_GRID {
      ...LogoGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE_CAROUSEL {
      ...ImageCarousel
    }
    ... on STRAPI__COMPONENT_SECTIONS_STATS {
      ...Stats
    }
    ... on STRAPI__COMPONENT_SECTIONS_CONTACT {
      ...Contact
    }
    ... on STRAPI__COMPONENT_SECTIONS_CAREERS {
      ...Careers
    }
    ... on STRAPI__COMPONENT_SECTIONS_CAREERS_ATS {
      ...CareersAts
    }
    ... on STRAPI__COMPONENT_SECTIONS_PRICING {
      ...Pricing
    }
    ... on STRAPI__COMPONENT_SECTIONS_PRICING_GRID {
      ...PricingGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_CONTACT_FORM {
      ...ContactForm
    }
    ... on STRAPI__COMPONENT_SECTIONS_DYNAMIC_TITLE {
      ...DynamicTitle
    }
    ... on STRAPI__COMPONENT_SECTIONS_LOGO_CAROUSEL {
      ...LogoCarousel
    }
    ... on STRAPI__COMPONENT_SECTIONS_CUSTOM_HTML {
      ...CustomHtml
    }
    ... on STRAPI__COMPONENT_SECTIONS_PORTFOLIO_GRID {
      ...PortfolioGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_ARTICLE {
      ...Article
    }
    ... on STRAPI__COMPONENT_SECTIONS_QUOTE_CAROUSEL {
      ...QuoteCarousel
    }
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE_TEXT_CAROUSEL {
      ...ImageTextCarousel
    }
    ... on STRAPI__COMPONENT_SECTIONS_FAQS {
      ...Faqs
    }
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE_GRID {
      ...ImageGrid
    }
    ... on STRAPI__COMPONENT_SECTIONS_IMAGE_CARDS {
      ...ImageCards
    }
    ... on STRAPI__COMPONENT_SECTIONS_BIG_IMAGE_CARDS {
      ...BigImageCards
    }
    ... on STRAPI__COMPONENT_SECTIONS_CTA_INFO_CARDS {
      ...CtaInfoCards
    }
    ... on STRAPI__COMPONENT_SECTIONS_INTERACTIVE_INFO_CARDS {
      ...InteractiveInfoCards
    }
    ... on STRAPI__COMPONENT_SECTIONS_TEXT {
      ...Text
    }
    ... on STRAPI__COMPONENT_SECTIONS_THIN_CTA {
      ...ThinCta
    }
    ... on STRAPI__COMPONENT_SECTIONS_FLEX_COLUMNS {
      ...FlexColumns
    }
    ... on STRAPI__COMPONENT_SECTIONS_TOGGLE_LIST {
      ...ToggleList
    }
    ... on STRAPI__COMPONENT_SECTIONS_CUSTOM_SECTION {
      ...CustomSection
    }
  }
`
